import { useId, useState, type ReactNode } from 'react';
import './card.css';

const KEY = 'kontor:card:';

function read(storageKey: string | undefined, fallback: boolean): boolean {
  if (!storageKey) return fallback;
  try {
    const value = window.localStorage.getItem(KEY + storageKey);
    return value === null ? fallback : value === '1';
  } catch {
    return fallback;
  }
}

function write(storageKey: string | undefined, open: boolean): void {
  if (!storageKey) return;
  try {
    window.localStorage.setItem(KEY + storageKey, open ? '1' : '0');
  } catch {
    // Der Browser erlaubt kein Speichern, dann gilt der Zustand nur für diese Sitzung
  }
}

interface Props {
  title: string;
  ariaLabel?: string;
  className?: string;
  /** Merkt sich pro Browser, ob der Bereich offen war */
  storageKey?: string;
  /** Vorgabe ohne gemerkten Zustand. Bereiche unter dem Kopf einer Karte sind zu, bis man auf den Titel klickt. */
  defaultOpen?: boolean;
  /** Von außen gesteuert, zum Beispiel wenn ein Knopf im Bereich ihn öffnet */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Schaltflächen rechts neben dem Titel, sie bleiben auch zugeklappt sichtbar */
  actions?: ReactNode;
  children: ReactNode;
}

/**
 * Bereich einer Karte, der sich per Klick auf den Titel auf- und zuklappen lässt, am Desktop wie auf dem Handy. Der Inhalt wird erst beim ersten Öffnen
 * aufgebaut, damit zugeklappte Bereiche nichts laden. Der Titel ist ein Knopf (aria-expanded), die Tastatur bedient ihn wie jeden anderen.
 */
export function CollapsibleCard({ title, ariaLabel, className = '', storageKey, defaultOpen = false, open: controlled, onOpenChange, actions, children }: Props) {
  const [inner, setInner] = useState(() => read(storageKey, defaultOpen));
  const open = controlled ?? inner;
  const [seen, setSeen] = useState(open);
  const bodyId = useId();
  if (open && !seen) setSeen(true);

  const toggle = () => {
    const next = !open;
    if (controlled === undefined) {
      setInner(next);
      write(storageKey, next);
    }
    onOpenChange?.(next);
  };

  return (
    <section className={`card ccard${open ? ' is-open' : ''} ${className}`.trim()} aria-label={ariaLabel ?? title}>
      <div className="ccard__head">
        <h2 className="ccard__title">
          <button type="button" className="ccard__toggle" aria-expanded={open} aria-controls={bodyId} onClick={toggle}>
            <svg className="ccard__chevron" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m9 6 6 6-6 6" />
            </svg>
            <span>{title}</span>
          </button>
        </h2>
        {actions && <div className="ccard__actions">{actions}</div>}
      </div>
      <div id={bodyId} className="ccard__body" hidden={!open}>
        {seen ? children : null}
      </div>
    </section>
  );
}
